import { useCallback, useEffect, useMemo, useState } from 'react'
import { ContentLoading } from '../../../../components/ContentLoading'
import apinode from '../../../../services/apinode'
import { sendErrorApiMessage } from '../../../../utils/sendErrorApiMessage'
import Navbar from '../../../../components/navbar/navbar'
import { useOrdenation } from '../../../../hooks/useOrdenation'
import { useNavigate } from 'react-router-dom'

// ─── Status badge ─────────────────────────────────────────────────────────────

const statusColors = {
  'ARMAZENADO COM FALTA': { bg: '#fee2e2', color: '#991b1b' },
  'AGUARDANDO SEPARAÇÃO': { bg: '#fef3c7', color: '#92400e' },
  'ARMAZENADO COMPLETO': { bg: '#d1fae5', color: '#065f46' },
  ENTREGUE: { bg: '#ede9fe', color: '#5b21b6' },
}

const StatusBadge = ({ label }) => {
  const style = statusColors[label] || { bg: '#f0ede8', color: '#444' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background: style.bg,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

const statCardColors = {
  'TOTAL DE PEDIDOS': { accent: '#c8a030', text: '#1a1a1a' },
  'AGUARDANDO SEPARAÇÃO': { accent: '#F59E0B', text: '#92400e' },
  'ARMAZENADO COM FALTA': { accent: '#ef4444', text: '#991b1b' },
  'ARMAZENADO COMPLETO': { accent: '#10B981', text: '#065f46' },
  ENTREGUE: { accent: '#6366F1', text: '#5b21b6' },
}

const StatCard = ({ label, value }) => {
  const colors = statCardColors[label] || { accent: '#c8a030', text: '#1a1a1a' }
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid #e8e6e0`,
        borderTop: `3px solid ${colors.accent}`,
        borderRadius: 10,
        padding: '20px 22px',
        flex: 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 36,
          fontWeight: 700,
          color: colors.text,
          lineHeight: 1,
          marginBottom: 6,
        }}
      >
        {value ?? 0}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#999',
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
    </div>
  )
}

// ─── Bar Row ──────────────────────────────────────────────────────────────────

const barColors = {
  'AGUARDANDO SEPARAÇÃO': '#F59E0B',
  'ARMAZENADO COM FALTA': '#ef4444',
  'ARMAZENADO COMPLETO': '#10B981',
  ENTREGUE: '#6366F1',
}

const BarRow = ({ label, value, total }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  const color = barColors[label] || '#c8a030'
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#666',
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: 12, color: '#888' }}>
          <span style={{ fontWeight: 700, color }}>{value}</span>{' '}
          <span style={{ color: '#bbb' }}>({pct}%)</span>
        </span>
      </div>
      <div
        style={{
          height: 7,
          background: '#f0ede8',
          borderRadius: 99,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: color,
            borderRadius: 99,
            transition: 'width 0.6s ease',
          }}
        />
      </div>
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LojaDashboard() {
  const [pedidosRecentes, setPedidosRecentes] = useState([])
  const [isFetchLoading, setIsFetchLoading] = useState(true)
  const [filtroPedido, setFiltroPedido] = useState('')
  const [filtroCliente, setFiltroCliente] = useState('')

  const navigate = useNavigate()

  const pedidosFiltrados = useMemo(() => {
    let data = pedidosRecentes

    if (filtroPedido) {
      data = data.filter(
        (p) =>
          p.idpedidoloja.includes(filtroPedido) ||
          p.pedido_matriz.includes(filtroPedido),
      )
    }

    if (filtroCliente) {
      data = data.filter((p) =>
        p.cliente.toLowerCase().includes(filtroCliente.toLowerCase()),
      )
    }

    return data
  }, [filtroCliente, filtroPedido, pedidosRecentes])

  const { filteredItems, Th, Paginate } = useOrdenation(
    pedidosFiltrados,
    'data_pedido',
    'desc',
  )

  const fetchData = useCallback(async () => {
    try {
      setIsFetchLoading(true)

      const pedidosRes = await apinode.get(
        `/loja/pedidos/${localStorage.idconta}`,
      )

      setPedidosRecentes(pedidosRes.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao carregar o dashboard')
    } finally {
      setIsFetchLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const totalPedidos = pedidosRecentes.length
  const aguardandoSeparacao = pedidosRecentes.filter(
    (p) => p.status_nome === 'AGUARDANDO SEPARAÇÃO',
  ).length
  const armazenadoComFalta = pedidosRecentes.filter(
    (p) => p.status_nome === 'ARMAZENADO COM FALTA',
  ).length
  const armazenadoCompleto = pedidosRecentes.filter(
    (p) => p.status_nome === 'ARMAZENADO COMPLETO',
  ).length
  const entregue = pedidosRecentes.filter(
    (p) => p.status_nome === 'ENTREGUE',
  ).length

  const statCards = [
    { label: 'TOTAL DE PEDIDOS', value: totalPedidos },
    { label: 'AGUARDANDO SEPARAÇÃO', value: aguardandoSeparacao },
    { label: 'ARMAZENADO COM FALTA', value: armazenadoComFalta },
    { label: 'ARMAZENADO COMPLETO', value: armazenadoCompleto },
    { label: 'ENTREGUE', value: entregue },
  ]

  const barRows = [
    { label: 'AGUARDANDO SEPARAÇÃO', value: aguardandoSeparacao },
    { label: 'ARMAZENADO COM FALTA', value: armazenadoComFalta },
    { label: 'ARMAZENADO COMPLETO', value: armazenadoCompleto },
    { label: 'ENTREGUE', value: entregue },
  ]

  return (
    <>
      <Navbar />

      <div
        className="mt-page"
        style={{
          display: 'flex',
          minHeight: '100vh',
          background: '#f5f4f0',
          fontSize: 14,
          flex: 1,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <ContentLoading
            isLoading={isFetchLoading}
            loadingText="Carregando dashboard..."
          >
            <div style={{ padding: 28, flex: 1 }}>
              {/* Stat Cards */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                {statCards.map((card) => (
                  <StatCard
                    key={card.label}
                    label={card.label}
                    value={card.value}
                  />
                ))}
              </div>

              {/* Distribuição por Status */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e8e6e0',
                  borderRadius: 10,
                  padding: '22px 24px',
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#1a1a1a',
                    marginBottom: 20,
                  }}
                >
                  Distribuição por Status
                </div>
                {barRows.map((row) => (
                  <BarRow
                    key={row.label}
                    label={row.label}
                    value={row.value}
                    total={pedidosRecentes.length}
                  />
                ))}
              </div>

              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e8e6e0',
                  borderRadius: 10,
                  padding: '22px 24px 0',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}
                  >
                    Pedidos Recentes
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      type="text"
                      placeholder="Nº Pedido..."
                      value={filtroPedido}
                      onChange={(e) => setFiltroPedido(e.target.value)}
                      style={{
                        padding: '7px 14px',
                        border: '1px solid #ddd',
                        borderRadius: 8,
                        fontSize: 13,
                        color: '#1a1a1a',
                        background: '#fafaf8',
                        outline: 'none',
                        width: 140,
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Cliente..."
                      value={filtroCliente}
                      onChange={(e) => setFiltroCliente(e.target.value)}
                      style={{
                        padding: '7px 14px',
                        border: '1px solid #ddd',
                        borderRadius: 8,
                        fontSize: 13,
                        color: '#1a1a1a',
                        background: '#fafaf8',
                        outline: 'none',
                        width: 180,
                      }}
                    />
                    <button
                      onClick={() => {
                        navigate('/loja/pedidos', {
                          state: {
                            shouldOpenModal: true,
                          },
                        })
                      }}
                      style={{
                        background: '#c8a030',
                        color: '#fff',
                        border: 'none',
                        padding: '7px 16px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      + Novo Pedido
                    </button>
                  </div>
                </div>

                <div style={{ marginLeft: -24, marginRight: -24 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8f7f3' }}>
                        {[
                          { label: 'Ped. Loja', value: 'idpedidoloja' },
                          { label: 'Ped. Matriz', value: 'pedido_matriz' },
                          { label: 'Cliente', value: 'cliente' },
                          { label: 'Data', value: 'data_pedido' },
                          { label: 'Entrega', value: 'data_entrega' },
                          { label: 'Cidade', value: 'cidade' },
                          { label: 'Status', value: 'status_nome' },
                        ].map((h) => (
                          <Th
                            key={h.value}
                            label={h.label}
                            value={h.value}
                            style={{
                              padding: '11px 16px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderTop: '1px solid #e8e6e0',
                              borderBottom: '1px solid #e8e6e0',
                              whiteSpace: 'nowrap',
                              background: '#f8f7f3',
                            }}
                          />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item, index) => (
                        <tr
                          key={item.id}
                          style={{
                            borderBottom:
                              index < filteredItems.length - 1
                                ? '1px solid #f0ede8'
                                : 'none',
                          }}
                        >
                          <td
                            style={{
                              padding: '14px 16px',
                              color: '#2a2a2a',
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {item.idpedidoloja}
                          </td>
                          <td
                            style={{
                              padding: '14px 16px',
                              color: '#555',
                              fontSize: 13,
                            }}
                          >
                            {item.pedido_matriz}
                          </td>
                          <td
                            style={{
                              padding: '14px 16px',
                              color: '#2a2a2a',
                              fontSize: 13,
                              maxWidth: 240,
                            }}
                          >
                            {item.cliente}
                          </td>
                          <td
                            style={{
                              padding: '14px 16px',
                              color: '#555',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.data_pedido
                              ? new Date(item.data_pedido).toLocaleDateString(
                                  'pt-BR',
                                )
                              : '—'}
                          </td>
                          <td
                            style={{
                              padding: '14px 16px',
                              color: '#555',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.data_entrega
                              ? new Date(item.data_entrega).toLocaleDateString(
                                  'pt-BR',
                                )
                              : '—'}
                          </td>
                          <td
                            style={{
                              padding: '14px 16px',
                              color: '#555',
                              fontSize: 13,
                            }}
                          >
                            {item.cidade ?? '—'}
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            {item.status_nome ? (
                              <StatusBadge label={item.status_nome} />
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}

                      {filteredItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            style={{
                              padding: '32px 16px',
                              textAlign: 'center',
                              color: '#aaa',
                              fontSize: 13,
                            }}
                          >
                            Nenhum pedido encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <Paginate />
                </div>
              </div>
            </div>
          </ContentLoading>
        </div>
      </div>
    </>
  )
}
